import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import * as _ from 'lodash';

import { StateService } from '@uirouter/angular';
import { WikiService } from '../services/wiki.service';
import { WikiHelperService } from '../services/wiki.helper';
import { UserService } from '../../../core/services/core.user.service';
import { UiService } from '../../../core/services/core.ui.service';
import { GfTranslateService } from '../../../core/services/core.translate.service';
import { WikiConflictService } from '../services/wiki.conflict.service';

@Component({
    selector: 'wiki-versions',
    template: require('./wiki.versions.component.html')
})
export class WikiVersionsComponent implements OnInit {
    /** Rejection marker for "restore conflicted and the user chose not to overwrite". */
    private static readonly CONFLICT_UNRESOLVED = 'wiki-restore-conflict-unresolved';


    @Input() section: string = '';
    @Input() refId: any = null;
    @Input() pageName: string = '';
    @Input() useUrlParams: boolean = true;

    @Output() restored = new EventEmitter<void>();

    protected versions = [];
    private restoreContent = '';
    private restoreContentType = '';
    protected version1: any = false;
    protected version2: any = false;
    protected loadingVersions = false;
    protected loadingContent = false;
    protected moreVersions = false;
    protected checkedCount = 0;
    protected hasUser: boolean = false;
    private restoreId: number = 0;
    protected wikiHtml = '';
    protected pageNum = 0;
    protected diff: any = null;
    protected versionAttachments: any[] = [];
    protected loadingVersionAttachments: boolean = false;

    constructor(
        private wikiService: WikiService,
        private wikiHelper: WikiHelperService,
        private userService: UserService,
        private stateService: StateService,
        private uiService: UiService,
        private gfTranslateService: GfTranslateService,
        private wikiConflictService: WikiConflictService,
    ) { }
    
    ngOnInit() {
        this.hasUser = this.userService.session.hasUser();
        this.fetch(this.pageNum, 20);
    };

    protected itemCheck = (version) => {
        var checkedVersions = this.getCheckedVersions();

        // Are we adding or removing this version?
        if (version.checked) {
            // It's checked, we're removing it
            version.checked = false;
        } else {
            // Not yet checked, we're adding it

            // If we already have two checked versions,
            // keep the latest version of the two.
            if (checkedVersions.length > 1) {
                checkedVersions[1].checked = false;
            }
            version.checked = true;
        }

        // Update $state
        checkedVersions = this.getCheckedVersions();
        this.checkedCount = checkedVersions.length;

        if (this.useUrlParams) {
            var params = {
                v1: _.get(checkedVersions, '[1].pageVersion', ''),
                v2: _.get(checkedVersions, '[0].pageVersion', '')
            };
            this.stateService.go('.', params, { reload: false, notify: false, location: true }).then(() => this.showContent(), () => {});
        } else {
            this.showContent();
        }
    };

    protected fetch = (page_num, page_size?) => {

        this.loadingVersions = true;
        this.moreVersions = false;

        page_size = page_size || 20;

        var params = {
            section: this.section,
            refId: this.refId,
            page_num: page_num,
            page_size: page_size
        };

        this.wikiService.versions(this.pageName, params)
        .then((data) => {
            this.versions = this.sortVersions(
                this.setCheckedStatus(
                    this.versions.concat(_.get(data, 'items', []))
                )
            );

            if (_.get(data, 'items.length', 0) == page_size) {
                this.moreVersions = true;
            }
        }, (response) => {
            console.log(response);
        })
        .then(() => {
            this.loadingVersions = false;
            this.pageNum = page_num;
        });
    };

    /**
     * The page's current version as of when this list was fetched. Sent as `baseVersion` on
     * restore so that restoring is rejected — rather than silently clobbering — if someone
     * saved the page while the user was browsing the history. `versions` is sorted descending.
     */
    private get currentVersion(): number {
        return _.get(this.versions, '[0].pageVersion', 0);
    }

    protected restore = () => {
        const restoringVersion = this.version1 || this.version2;
        var params: any = {
            content: this.restoreContent,
            pagename: this.pageName,
            section: this.section,
            refId: this.refId,
            restoreversion: restoringVersion ? restoringVersion.pageVersion : null
        };

        if (restoringVersion) {
            params.changeSummary = this.gfTranslateService.translate('Restored version') + ' #' + restoringVersion.pageVersion;
        }

        // Preserve the content type of the restored version
        if (this.restoreContentType) {
            params.content_type = this.restoreContentType;
        }

        // A restore is always an update, so the server requires this. Guarded only so a version
        // list that failed to load produces the clearer "required" error rather than a
        // "must be a positive integer" one for a baseVersion of 0.
        if (this.currentVersion > 0) {
            params.baseVersion = this.currentVersion;
        }

        const restoreId = this.restoreId;

        this.wikiService.update(restoreId, params)
        .catch((error: any) => {
            const conflict = this.wikiConflictService.parse(error);
            if (!conflict) { return Promise.reject(error); }

            return this.wikiConflictService.open(conflict, restoreId).then((choice) => {
                if (choice !== 'overwrite') {
                    return Promise.reject(WikiVersionsComponent.CONFLICT_UNRESOLVED);
                }
                return this.wikiService.update(restoreId, { ...params, baseVersion: conflict.currentVersion });
            });
        })
        .then((response) => {
            this.uiService.message.showSuccess('Wiki page restored successfully');
            if (this.useUrlParams) {
                this.stateService.reload();
            } else {
                this.restored.emit();
            }
        })
        .catch((response) => {
            // The conflict modal has already explained a declined overwrite; anything else is a
            // genuine failure worth reporting.
            if (response === WikiVersionsComponent.CONFLICT_UNRESOLVED) { return; }
            this.uiService.message.showError(this.gfTranslateService.translate('An error occured while restoring the wiki page: ') + response.statusText);
        });
    };

    private getCheckedVersions() {
        var ret = this.sortVersions(
            this.versions.filter((item) => item.checked === true)
        );
        this.version1 = _.get(ret, '[1]', false);
        this.version2 = _.get(ret, '[0]', false);

        return ret;
    }

    sortVersions(versions) {
        return _.sortBy(versions, 'pageVersion').reverse();
    }

    setCheckedStatus(versions) {
        if (!this.useUrlParams) {
            return _.map(versions, v => { v.checked = false; return v; });
        }

        var selectedVersions = _.filter(
            _.get(this.stateService.params, 'v1', false),
            _.get(this.stateService.params, 'v2', false)
        );
        this.checkedCount = selectedVersions.length;

        return _.map(
            versions,
            function (v) {
                v.checked = selectedVersions.includes(v.pageVersion);
                return v;
            }
        );
    }

    showContent() {
        this.diff = false;
        this.versionAttachments = [];

        if (this.version1) {
            this.showDiff();
        } else if (this.version2) {
            this.showVersion();
        }
    }

    showVersion() {
        this.loadingContent = true;
        this.loadingVersionAttachments = true;
        this.versionAttachments = [];
        this.wikiHtml = '';
        this.restoreId = 0;
        this.restoreContent = '';
        this.restoreContentType = '';

        var params = {
            section: this.section,
            refId: this.refId,
            editable: true,
            version: this.version2.pageVersion
        };

        const urlFn = () => '';

        this.wikiService.page(this.pageName, params)
        .then((response) => {
            this.restoreId = _.get(response, 'id', 0);
            this.wikiHtml = this.wikiHelper.convertWikiWords(_.get(response, 'data', ''), urlFn);
            this.restoreContent = _.get(response, 'editableContent', '');
            this.restoreContentType = _.get(response, 'content_type', '');

            if (this.restoreId) {
                return this.wikiService.getAttachments(this.restoreId, this.version2.pageVersion)
                    .then((attachResponse: any) => {
                        this.versionAttachments = (attachResponse && attachResponse.items) ? attachResponse.items : [];
                    })
                    .catch(() => {})
                    .then(() => { this.loadingVersionAttachments = false; });
            }
            this.loadingVersionAttachments = false;
        }, (response) => {
            console.warn('wiki-versions: could not load page version', response);
            this.loadingVersionAttachments = false;
        })
        .then(() => { this.loadingContent = false; });
    }

    private showDiff() {
        this.loadingContent = true;

        var params = {
            section: this.section,
            refId: this.refId,
            version1: this.version1.pageVersion,
            version2: this.version2.pageVersion
        };

        this.wikiService.diff(this.pageName, params)
        .then((response) => {
            // Add summary data to each diff
            var diffs = _.map(
                _.get(response, 'diff', []),
                    function (diff) {
                        diff.added = _.reduce(diff.lines, function (carry, line) {
                            if (line.type == 'new') {
                                carry++;
                            }
                            return carry;
                        }, 0);
                        diff.removed = _.reduce(diff.lines, function (carry, line) {
                            if (line.type == 'old') {
                                carry++;
                            }
                            return carry;
                        }, 0);
                        return diff;
                    }
                );

                if (diffs && diffs.length > 0) {
                    diffs[0].fileName = this.pageName +
                        ": versions " +
                        this.version1.pageVersion +
                        " => " +
                        this.version2.pageVersion;
                }
                this.diff = diffs;
        }, (response) => {
            this.uiService.message.showError(
                this.gfTranslateService.translate('Sorry, could not load the diff content for the specified versions'),
                this.gfTranslateService.translate('Wiki Diff')
            );
        })
        .then(() => { this.loadingContent = false; });
    }
}
